import React from 'react';
import { AddNewNodeIcon, DeleteIcon, ColorPickerIcon, CenterIcon, FocusIcon } from './icons';

function NodeContextMenu({
  isDarkMode,
  onAddChild,
  onAddSibling,
  onAddTaskChild,
  onDelete,
  onChangeColor,
  onCenterNode,
  onFocusBranch,
  onOpenBranch,
  onCopyLink,
  isTask = false,
  onToggleTask,
  onChangeEmoji,
  onCreateMindmapLink,
  canCreateMindmapLink = true,
  canAddSibling = true,
  canWrite = true,
}) {
  const bg = isDarkMode ? '#1f2933' : '#ffffff';
  const border = isDarkMode ? '#4b5563' : '#e5e7eb';
  const text = isDarkMode ? '#e5e7eb' : '#111827';
  const hoverBg = isDarkMode ? '#374151' : '#f3f4f6';

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: 13,
    color: text,
    borderRadius: 4,
    minHeight: 44,
    boxSizing: 'border-box',
  };

  return (
    <div
      role="menu"
      aria-label="Node actions"
      style={{
        minWidth: 190,
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        boxShadow:
          '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.25)',
        padding: 6,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      onClick={(e) => {
        // Prevent clicks inside the menu from triggering
        // the container's onClick handler (which would
        // schedule hiding the color picker).
        e.stopPropagation();
      }}
    >
      {canWrite && (
        <>
          <div
            style={itemStyle}
            onClick={(e) => {
              e.stopPropagation();
              onAddChild && onAddChild();
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <AddNewNodeIcon />
            <span>Add child node</span>
          </div>
          {canAddSibling && (
            <div
              style={itemStyle}
              onClick={(e) => {
                e.stopPropagation();
                onAddSibling && onAddSibling();
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <AddNewNodeIcon />
              <span>Add sibling node (Enter / Shift+Tab)</span>
            </div>
          )}
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onAddTaskChild && onAddTaskChild();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        {/* Checkbox-like glyph for "add task" */}
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: 3,
            border: `1.5px solid ${text}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
          }}
        >
          +
        </span>
        <span>Add task node</span>
      </div>
      <div
        style={{
          height: 1,
          margin: '4px 0',
          backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
        }}
      />
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onToggleTask && onToggleTask();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        {/* Simple checkbox-like glyph for node task flag */}
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: 3,
            border: `1.5px solid ${text}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
          }}
        >
          {isTask ? '✓' : ''}
        </span>
        <span>{isTask ? 'Remove task flag' : 'Convert to task'}</span>
      </div>
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onDelete && onDelete();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <DeleteIcon />
        <span>Delete node</span>
      </div>
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onChangeColor && onChangeColor();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <ColorPickerIcon />
        <span>Change color</span>
      </div>
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onChangeEmoji && onChangeEmoji();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <span style={{ fontSize: 16 }}>😊</span>
        <span>Emoji</span>
      </div>
          {canCreateMindmapLink && (
          <div
            style={itemStyle}
            onClick={(e) => {
              e.stopPropagation();
              onCreateMindmapLink && onCreateMindmapLink();
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <img
              src="/favicon-32x32.png"
              width="16"
              height="16"
              alt=""
              aria-hidden="true"
              style={{ display: 'block', borderRadius: 3, flexShrink: 0 }}
            />
            <span>New Map</span>
          </div>
          )}
        </>
      )}
      <div
        style={{
          height: 1,
          margin: '4px 0',
          backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
        }}
      />
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onCenterNode && onCenterNode();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <CenterIcon />
        <span>Center on this node</span>
      </div>
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onFocusBranch && onFocusBranch();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <FocusIcon />
        <span>Focus on this branch</span>
      </div>
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onOpenBranch && onOpenBranch();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <span style={{ fontSize: 16 }}>↗</span>
        <span>Open branch in new tab</span>
      </div>
      <div
        style={itemStyle}
        onClick={(e) => {
          e.stopPropagation();
          onCopyLink && onCopyLink();
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        {/* Simple link-like glyph */}
        <span style={{ fontSize: 16 }}>🔗</span>
        <span>Copy link to this branch</span>
      </div>
    </div>
  );
}

export default NodeContextMenu;
